import classNames from 'classnames'
import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { mergeRefs } from 'react-merge-refs'
import { resolveFluentContent } from '../ButtonLike'
import * as styles from './styles'
import { type QuasiTabsOptionProps, type QuasiTabsProps } from './types'

export { selectors as quasiTabsSelectors } from './styles'

type ContextValue = Readonly<{
  handleKeyDown: React.KeyboardEventHandler<HTMLButtonElement>
  isCheckedOptionFocusable: boolean
  selectValue: (value: unknown) => void
  selectedValue: unknown
  size: NonNullable<QuasiTabsProps['size']>
}>

// `null` means this is not used within `<QuasiTabs>`
const QuasiTabsContext = createContext<ContextValue | null>(null)

const OPTION_SELECTOR = '[role=radio]:not([disabled])'
const FOCUSABLE_CHECKED_SELECTOR = `${OPTION_SELECTOR}[aria-checked=true]`

/**
 * Handles the arrow keys, Home, and End
 */
function resolveNextIndex(
  key: string,
  currentIndex: number,
  count: number,
  isRightToLeft: boolean
): number | undefined {
  // Wrap at both ends.
  const step = (offset: number): number =>
    (currentIndex + offset + count) % count
  const inlineStep = isRightToLeft ? -1 : 1

  switch (key) {
    case 'ArrowDown':
      return step(1)
    case 'ArrowUp':
      return step(-1)
    case 'ArrowRight':
      return step(inlineStep)
    case 'ArrowLeft':
      return step(-inlineStep)
    case 'Home':
      return 0
    case 'End':
      return count - 1
    default:
      return undefined
  }
}

/**
 * @note Can only be used within `<QuasiTabs>`.
 * @note If you use the type argument, be careful to match it with the one on `<QuasiTabs>`. The type system can’t
 * enforce it for you.
 */
const Option = forwardRef<HTMLButtonElement, QuasiTabsOptionProps<unknown>>(
  function Option(props, ref): JSX.Element {
    const context = useContext(QuasiTabsContext)
    if (context === null) {
      throw new TypeError(
        'QuasiTabs.Option: must be rendered inside <QuasiTabs>'
      )
    }

    const { children, className, glyph, value, ...remainingProps } = props
    const {
      handleKeyDown,
      isCheckedOptionFocusable,
      selectValue,
      selectedValue,
      size,
    } = context
    const isChecked = selectedValue === value

    return (
      <button
        className={classNames(styles.selectors.option, className)}
        css={styles.option}
        data-isicononly={glyph && !children}
        data-size={size}
        ref={ref}
        type="button"
        {...remainingProps}
        aria-checked={isChecked}
        onClick={() => selectValue(value)}
        onKeyDown={handleKeyDown}
        role="radio"
        // Native radio behavior: the checked option is the group's one tab stop, but when there's no focusable checked
        // option (nothing checked, the checked option is disabled, or a controlled `value` matches no option) every
        // option is a tab stop.
        tabIndex={isChecked || !isCheckedOptionFocusable ? 0 : -1}
      >
        {resolveFluentContent(
          { children, glyph, ...remainingProps },
          'QuasiTabs.Option'
        )}
      </button>
    )
  }
) as (<T = string>(
  props: QuasiTabsOptionProps<T> & React.RefAttributes<HTMLButtonElement>
) => JSX.Element | null) &
  Pick<React.NamedExoticComponent, 'displayName'>
Option.displayName = 'Lapidary: QuasiTabs.Option'

/**
 * UI that *looks* like tabs, but behaves as a radio group.
 *
 * If at all possible you should use real tabs (`<ViewTabs>`, `<SidebarTabs>`); this is only meant for older UI styles
 * that need tabs, but don’t work with those components.
 *
 * @example
 * <QuasiTabs aria-label="Calendar view" onValueChange={setMode} value={mode}>
 *   <QuasiTabs.Option value="day">Day</QuasiTabs.Option>
 *   <QuasiTabs.Option value="week">Week</QuasiTabs.Option>
 * </QuasiTabs>
 *
 * @note By default this is an uncontrolled component that manages its own selection state. If the `value` prop is set
 * it becomes controlled, and you’re responsible for passing in the new state after `onValueChange` fires.
 * @note You MUST NOT give `<QuasiTabs>` any children other than `<QuasiTabs.Option>`s.
 * @note If you use the type argument, be careful to also specify it on all `<QuasiTabs.Option>`s. The type system can’t
 * enforce it for you.
 */
export const QuasiTabs = Object.assign(
  forwardRef<HTMLDivElement, QuasiTabsProps<unknown>>(
    function QuasiTabs(props, ref) {
      const {
        className,
        defaultValue,
        onValueChange,
        size = 'regular',
        value,
        ...remainingProps
      } = props

      const [uncontrolledValue, setUncontrolledValue] =
        useState<unknown>(defaultValue)
      const isControlled = value !== undefined
      const selectedValue = isControlled ? value : uncontrolledValue

      const selectValue = useCallback(
        (nextValue: unknown) => {
          if (nextValue === selectedValue) return
          if (!isControlled) setUncontrolledValue(nextValue)
          onValueChange?.(nextValue)
        },
        [isControlled, onValueChange, selectedValue]
      )

      // The type system can't statically restrict `children` to `<QuasiTabs.Option>`, so manually enforce it at
      // runtime in dev
      const localRef = useRef<HTMLDivElement>(null)

      const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLButtonElement>): void => {
          const group = localRef.current
          if (group === null) return

          const options = [
            ...group.querySelectorAll<HTMLButtonElement>(OPTION_SELECTOR),
          ]
          const currentIndex = options.indexOf(event.currentTarget)
          if (currentIndex === -1) return

          const nextIndex = resolveNextIndex(
            event.key,
            currentIndex,
            options.length,
            group.matches(':dir(rtl)')
          )
          const nextOption =
            nextIndex === undefined ? undefined : options[nextIndex]
          if (nextOption === undefined) return

          // The arrow keys would otherwise scroll the page.
          event.preventDefault()
          nextOption.focus()
          nextOption.click()
        },
        []
      )

      // Whether the checked option exists and is enabled can only be known from the DOM, because options are arbitrary
      // children that may change without the group re-rendering.
      const [isCheckedOptionFocusable, setIsCheckedOptionFocusable] =
        useState(true)
      useEffect(() => {
        const group = localRef.current
        if (group === null) return

        const check = (): void =>
          setIsCheckedOptionFocusable(
            group.querySelector(FOCUSABLE_CHECKED_SELECTOR) !== null
          )
        check()

        const observer = new MutationObserver(check)
        observer.observe(group, {
          attributeFilter: ['aria-checked', 'disabled'],
          childList: true,
          subtree: true,
        })
        return () => observer.disconnect()
      }, [])

      const contextValue = useMemo(
        () => ({
          handleKeyDown,
          isCheckedOptionFocusable,
          selectValue,
          selectedValue,
          size,
        }),
        [
          handleKeyDown,
          isCheckedOptionFocusable,
          selectValue,
          selectedValue,
          size,
        ]
      )
      useEffect(() => {
        if (process.env.NODE_ENV === 'production' || localRef.current === null)
          return

        for (const child of localRef.current.children) {
          if (!child.classList.contains(styles.selectors.option)) {
            throw new TypeError(
              'QuasiTabs: every child must be a <QuasiTabs.Option>'
            )
          }
        }
      })

      return (
        <QuasiTabsContext.Provider value={contextValue}>
          <div
            css={styles.root}
            ref={mergeRefs([localRef, ref])}
            {...remainingProps}
            // Radio groups are vertical by default
            aria-orientation="horizontal"
            className={classNames(styles.selectors.root, className)}
            data-size={size}
            role="radiogroup"
          />
        </QuasiTabsContext.Provider>
      )
    }
  ) as (<T = string>(
    props: QuasiTabsProps<T> & React.RefAttributes<HTMLDivElement>
  ) => JSX.Element | null) &
    Pick<React.NamedExoticComponent, 'displayName'>,
  { Option }
)
QuasiTabs.displayName = 'Lapidary: QuasiTabs'
